class MessgrowHero extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-mg-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-mg-dot]'));
    this.track = this.querySelector('.mg-hero__track');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.autoplaySeconds || 0) * 1000;
    this.pointerStart = null;
    this.suppressNextClick = false;
    this.previousButton = this.querySelector('[data-mg-prev]');
    this.nextButton = this.querySelector('[data-mg-next]');

    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);
    this.handlePointerEnd = this.onPointerEnd.bind(this);
    this.handleClick = this.onClick.bind(this);
    this.handlePrevious = this.onPrevious.bind(this);
    this.handleNext = this.onNext.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleBlockSelect = this.onBlockSelect.bind(this);
    this.handleTrackScroll = this.onTrackScroll.bind(this);
    this.handleResize = this.onResize.bind(this);
    this.handleMouseEnter = this.stop.bind(this);
    this.handleMouseLeave = this.start.bind(this);
    this.handleFocusIn = this.stop.bind(this);
    this.handleFocusOut = this.start.bind(this);
    this.handleDotClicks = this.dots.map((dot, index) => {
      const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.show(index, true);
      };
      dot.addEventListener('click', handler);
      return { dot, handler };
    });

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointermove', this.handlePointerMove, { passive: false });
    this.addEventListener('pointerup', this.handlePointerEnd);
    this.addEventListener('pointercancel', this.handlePointerEnd);
    this.addEventListener('click', this.handleClick, true);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    if (this.slides.length > 1) this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerup', this.handlePointerEnd);
    this.removeEventListener('pointercancel', this.handlePointerEnd);
    this.removeEventListener('click', this.handleClick, true);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.handleDotClicks?.forEach(({ dot, handler }) => dot.removeEventListener('click', handler));
    this.track?.removeEventListener('scroll', this.handleTrackScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.initialized = false;
  }

  isMobileLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  onPrevious(event) {
    event.preventDefault();
    event.stopPropagation();
    this.show(this.index - 1, true);
  }

  onNext(event) {
    event.preventDefault();
    event.stopPropagation();
    this.show(this.index + 1, true);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
  }

  onBlockSelect(event) {
    const selectedSlide = this.slides.find((slide) => (
      slide === event.target
      || slide.contains(event.target)
      || slide.dataset.blockId === event.detail?.blockId
    ));
    if (!selectedSlide) return;

    this.stop();
    this.show(this.slides.indexOf(selectedSlide));
  }

  onTrackScroll() {
    if (!this.isMobileLayout() || !this.track?.clientWidth) return;

    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      const nextIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.activate(nextIndex);
    });
  }

  onResize() {
    if (!this.track) return;

    if (this.isMobileLayout()) {
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'auto' });
    } else {
      this.track.scrollLeft = 0;
    }
  }

  onPointerDown(event) {
    if (this.isMobileLayout() || !event.isPrimary || this.slides.length < 2) return;
    if (event.target.closest?.('a, button, input, select, textarea, [role="button"]')) return;

    this.pointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY
    };
    this.stop();
    this.setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event) {
    if (this.isMobileLayout()) return;
    if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

    const horizontalDistance = event.clientX - this.pointerStart.x;
    const verticalDistance = event.clientY - this.pointerStart.y;
    if (Math.abs(horizontalDistance) > 12 && Math.abs(horizontalDistance) > Math.abs(verticalDistance)) {
      event.preventDefault();
    }
  }

  onPointerEnd(event) {
    if (this.isMobileLayout()) return;
    if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

    const horizontalDistance = event.clientX - this.pointerStart.x;
    const verticalDistance = event.clientY - this.pointerStart.y;
    const isSwipe = Math.abs(horizontalDistance) >= 46
      && Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.15;

    this.releasePointerCapture?.(event.pointerId);
    this.pointerStart = null;

    if (isSwipe) {
      this.suppressNextClick = true;
      window.setTimeout(() => {
        this.suppressNextClick = false;
      }, 500);
      this.show(this.index + (horizontalDistance < 0 ? 1 : -1), true);
    } else {
      this.start();
    }
  }

  onClick(event) {
    if (!this.suppressNextClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.suppressNextClick = false;
  }

  show(nextIndex, userInitiated = false) {
    if (!this.slides.length) return;
    const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;
    this.activate(normalizedIndex);

    if (this.isMobileLayout() && this.track) {
      this.track.scrollTo({
        left: normalizedIndex * this.track.clientWidth,
        behavior: userInitiated ? 'smooth' : 'auto'
      });
    }

    if (userInitiated) {
      this.stop();
      this.start();
    }
  }

  activate(nextIndex) {
    if (!this.slides.length) return;
    this.index = Math.max(0, Math.min(nextIndex, this.slides.length - 1));

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  start() {
    if (
      window.Shopify?.designMode
      || !this.interval
      || this.slides.length < 2
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('messgrow-hero')) {
  customElements.define('messgrow-hero', MessgrowHero);
}

class MessgrowProductSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.viewport = this.querySelector('[data-mg-product-viewport]');
    this.grid = this.querySelector('.mg-product-grid');
    this.previousButton = this.querySelector('[data-mg-product-prev]');
    this.nextButton = this.querySelector('[data-mg-product-next]');
    if (!this.viewport || !this.grid) return;

    this.initialized = true;
    this.handleScroll = () => window.requestAnimationFrame(() => this.updateButtons());
    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateButtons());
    this.resizeObserver.observe(this.viewport);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
  }

  move(direction) {
    const firstCard = this.grid.firstElementChild;
    if (!firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = Number.parseFloat(window.getComputedStyle(this.grid).columnGap) || 0;
    this.viewport.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
  }

  updateButtons() {
    const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const position = this.viewport.scrollLeft;

    if (this.previousButton) this.previousButton.disabled = position <= 2;
    if (this.nextButton) this.nextButton.disabled = position >= maximum - 2;
  }
}

if (!customElements.get('messgrow-product-slider')) {
  customElements.define('messgrow-product-slider', MessgrowProductSlider);
}

function enhanceMessgrowProductReviews(root = document) {
  const widgetRoot = root.querySelector?.('#etrusted-product_review_list');
  const block = widgetRoot?.closest('.product-details__block');

  if (!block || block.closest('.mg-product-reviews')) return;

  const details = document.createElement('details');
  const summary = document.createElement('summary');
  const heading = document.createElement('span');
  const headingText = document.createElement('strong');
  const subtitle = document.createElement('small');
  const toggle = document.createElement('span');
  const body = document.createElement('div');
  const labelSource = document.querySelector('[data-mg-reviews-label]');
  const subtitleSource = document.querySelector('[data-mg-reviews-subtitle]');

  details.className = 'mg-product-reviews';
  details.open = false;
  summary.className = 'mg-product-reviews__summary';
  heading.className = 'mg-product-reviews__heading';
  headingText.textContent = labelSource?.textContent?.trim() || 'Bewertungen';
  subtitle.textContent = subtitleSource?.textContent?.trim() || '';
  toggle.className = 'mg-product-reviews__toggle';
  toggle.setAttribute('aria-hidden', 'true');
  body.className = 'mg-product-reviews__body';

  heading.append(headingText, subtitle);
  summary.append(heading, toggle);
  summary.addEventListener('click', (event) => {
    event.preventDefault();
    details.open = !details.open;
  });
  block.parentNode.insertBefore(details, block);
  details.append(summary, body);
  body.appendChild(block);
  details.parentElement?.classList.add('mg-product-details-grid');
}

let messgrowReviewObserver;
let messgrowTrustedThemeObserver;

function syncMessgrowTrustedShopsTheme(root = document) {
  const dark = document.documentElement.dataset.mgTheme === 'dark';
  const scope = root?.querySelectorAll ? root : document;

  scope.querySelectorAll('etrusted-star-extension').forEach((widget) => {
    widget.setAttribute('data-theme', dark ? 'dark' : 'light');
    const shadow = widget.shadowRoot;
    if (!shadow) return;

    shadow.querySelector('etrusted-rl-star-rating')?.setAttribute('theme', dark ? 'dark' : 'light');

    let override = shadow.querySelector('[data-mg-trusted-theme]');
    if (!dark) {
      override?.remove();
      return;
    }

    if (!override) {
      override = document.createElement('style');
      override.dataset.mgTrustedTheme = 'true';
      shadow.appendChild(override);
    }
    override.textContent = '.grade,.count{color:#edf4ef!important;}';
  });
}

function scheduleMessgrowTrustedShopsTheme(root = document) {
  [0, 250, 1000, 3000].forEach((delay) => {
    window.setTimeout(() => syncMessgrowTrustedShopsTheme(root), delay);
  });
}

function initMessgrowCartDiscount(root = document) {
  root.querySelectorAll?.('[data-mg-discount]').forEach((container) => {
    if (container.dataset.mgDiscountReady === 'true') return;
    container.dataset.mgDiscountReady = 'true';

    const input = container.querySelector('[data-mg-discount-input]');
    const button = container.querySelector('[data-mg-discount-apply]');
    const error = container.querySelector('[data-mg-discount-error]');

    const apply = () => {
      const code = input?.value.trim();
      if (!code) {
        if (error) error.hidden = false;
        input?.focus();
        return;
      }

      if (error) error.hidden = true;
      const discountRoute = container.dataset.discountRoute || '/discount/';
      const cartRoute = container.dataset.cartRoute || '/cart';
      window.location.assign(`${discountRoute}${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartRoute)}`);
    };

    button?.addEventListener('click', apply);
    input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      apply();
    });
    input?.addEventListener('input', () => {
      if (error) error.hidden = true;
    });
  });
}

function initMessgrowAccountLinks(root = document) {
  root.querySelectorAll?.('[data-mg-account-link]').forEach((link) => {
    if (link.dataset.mgAccountReady === 'true') return;
    link.dataset.mgAccountReady = 'true';

    link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(link.href);
    });
  });
}

function initMessgrowEnhancements(root = document) {
  enhanceMessgrowProductReviews(root);
  initMessgrowDesktopNavigation(root);
  scheduleMessgrowTrustedShopsTheme(root);
  initMessgrowCartDiscount(root);
  initMessgrowAccountLinks(root);

  if (!messgrowTrustedThemeObserver && document.body) {
    messgrowTrustedThemeObserver = new MutationObserver((mutations) => {
      const trustedWidgetAdded = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches?.('etrusted-star-extension, etrusted-product-review-list-widget-product-star-extension')
          || node.querySelector?.('etrusted-star-extension, etrusted-product-review-list-widget-product-star-extension');
      }));

      if (trustedWidgetAdded) scheduleMessgrowTrustedShopsTheme(document);
    });
    messgrowTrustedThemeObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.querySelector('.cc-product-details') && !document.querySelector('.mg-product-reviews') && !messgrowReviewObserver) {
    messgrowReviewObserver = new MutationObserver(() => {
      enhanceMessgrowProductReviews(document);

      if (document.querySelector('.mg-product-reviews')) {
        messgrowReviewObserver.disconnect();
        messgrowReviewObserver = undefined;
      }
    });

    messgrowReviewObserver.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => {
      messgrowReviewObserver?.disconnect();
      messgrowReviewObserver = undefined;
    }, 12000);
  }
}

function setMessgrowDesktopSubmenu(menu, button, expanded) {
  const panelId = button.getAttribute('aria-controls');
  const panel = panelId ? menu.querySelector(`#${CSS.escape(panelId)}`) : null;
  if (!panel) return;

  const owner = button.closest('.main-nav__item--dropdown, li');
  button.setAttribute('aria-expanded', String(expanded));
  owner?.classList.toggle('is-open', expanded);
  panel.classList.toggle('is-open', expanded);
  panel.classList.toggle('is-visible', expanded);

  if (!expanded) {
    panel.querySelectorAll('.js-toggle[aria-expanded="true"]').forEach((nestedButton) => {
      setMessgrowDesktopSubmenu(menu, nestedButton, false);
    });
  }
}

function initMessgrowDesktopNavigation(root = document) {
  root.querySelectorAll?.('main-menu').forEach((menu) => {
    if (menu.dataset.messgrowDesktopNavigation === 'true') return;

    menu.dataset.messgrowDesktopNavigation = 'true';
    const menuToggle = menu.querySelector('.main-menu__toggle');
    const menuContent = menu.querySelector('.main-menu__content');
    let menuWasOpen = false;

    const syncMenuState = () => {
      const menuIsOpen = menuToggle?.getAttribute('aria-expanded') === 'true'
        || menuContent?.classList.contains('is-open');

      document.body.classList.toggle('mg-main-menu-open', Boolean(menuIsOpen));
      if (menuIsOpen && !menuWasOpen && menuContent) {
        menuContent.scrollTop = 0;
      }
      menuWasOpen = Boolean(menuIsOpen);
    };

    if (menuToggle) {
      new MutationObserver(syncMenuState).observe(menuToggle, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }
    if (menuContent) {
      new MutationObserver(syncMenuState).observe(menuContent, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    syncMenuState();

    menu.addEventListener('click', (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const backButton = event.target.closest('.js-back');
      if (backButton && menu.contains(backButton)) {
        const panel = backButton.closest('.main-nav__child, .child-nav__panel');
        const controller = panel?.id
          ? menu.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`)
          : null;

        if (!controller) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        setMessgrowDesktopSubmenu(menu, controller, false);
        controller.focus();
        return;
      }

      const toggleButton = event.target.closest('.main-nav__toggle-btn.js-toggle');
      if (!toggleButton || !menu.contains(toggleButton)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const expanded = toggleButton.getAttribute('aria-expanded') === 'true';
      setMessgrowDesktopSubmenu(menu, toggleButton, !expanded);
    }, true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMessgrowEnhancements());
} else {
  initMessgrowEnhancements();
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('messgrow-hero').forEach((hero) => {
    if (!hero.slides) hero.connectedCallback();
  });
  initMessgrowEnhancements(event.target);
});
